import { Loader } from "@googlemaps/js-api-loader";
import { IMapInstance, LatLng } from "../../maps/map-provider.interface";
import { GeoAddress, IGeoSearchProvider } from "../geo-search-provider.interface";
import { AutocompleteProvider } from "../../../types/enums";

export class GoogleGeoSearchProvider implements IGeoSearchProvider {
    private readonly _loader: Loader;

    // SearchBox mode (coords only)
    private _searchBoxListener: google.maps.MapsEventListener | null = null;
    private _boundsUnsub: (() => void) | null = null;

    // Autocomplete mode (GeoAddress)
    private _autocompleteListener: google.maps.MapsEventListener | null = null;

    // Geocoder cache
    private _geocoder: google.maps.Geocoder | null = null;

    constructor(loader: Loader) {
        this._loader = loader;
    }

    getGeoSearchProvider(): AutocompleteProvider {
        return AutocompleteProvider.GOOGLE;
    }

    async bindAutocompleteAsync(
        autoCompleteElement: HTMLElement,
        mapInstance: IMapInstance,
        onSelected: (coords: LatLng) => void,
        isNewVersion: boolean = false,
    ): Promise<void> {
        if (isNewVersion) {
            await this.bindAutoCompleteNewVersionAsync(autoCompleteElement, mapInstance, onSelected);
            return;
        }
        await this.bindAutocompleteAsyncOldVersion(autoCompleteElement as HTMLInputElement, mapInstance, onSelected);
    }

    async bindAutocompleteAsyncOldVersion(
        input: HTMLInputElement,
        mapInstance: IMapInstance,
        onSelected: (coords: LatLng) => void
    ): Promise<void> {
        // Ensure Places library is available (importLibrary is the modern path)
        await this._loader.importLibrary("places");

        // cleanup prior SearchBox binding only (do NOT affect address autocomplete)
        this._unbindSearchBox();

        const searchBox = new google.maps.places.SearchBox(input);

        // Bias results toward current viewport (optional)
        if (mapInstance.onBoundsChanged && mapInstance.getBoundsRect) {
            const bias = () => {
                const rect = mapInstance.getBoundsRect?.();
                if (!rect) return;

                const bounds = new google.maps.LatLngBounds(
                    { lat: rect.south, lng: rect.west },
                    { lat: rect.north, lng: rect.east }
                );

                searchBox.setBounds(bounds);
            };

            // set initial bias immediately
            bias();

            // store unsubscribe so dispose() can remove it cleanly
            this._boundsUnsub = mapInstance.onBoundsChanged(bias);
        }

        // Places changed
        this._searchBoxListener = searchBox.addListener("places_changed", () => {
            const places = searchBox.getPlaces() ?? [];
            if (places.length === 0) return;

            const place = places[0];
            const loc = place.geometry?.location;
            if (!loc) return;

            onSelected({ lat: loc.lat(), lng: loc.lng() });
        });
    }

    async bindAutoCompleteNewVersionAsync(
        containerElement: HTMLElement,
        mapInstance: IMapInstance,
        onSelected: (coords: LatLng) => void
    ): Promise<void> {
        await this._loader.importLibrary("places");

        const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({});
        containerElement.appendChild(placeAutocomplete);

        // Bias results toward current viewport (optional)
        if (mapInstance.onBoundsChanged && mapInstance.getBoundsRect) {
            const bias = () => {
                const rect = mapInstance.getBoundsRect?.();
                if (!rect) return;

                const bounds = new google.maps.LatLngBounds(
                    { lat: rect.south, lng: rect.west },
                    { lat: rect.north, lng: rect.east }
                );
                placeAutocomplete.locationBias = bounds;
            };

            // set initial bias immediately
            bias();

            // store unsubscribe so dispose() can remove it cleanly
            this._boundsUnsub = mapInstance.onBoundsChanged(bias);
        }

        const onPlaceSelect = async (event: Event) => {
            const selectEvent = event as google.maps.places.PlaceAutocompletePlaceSelectEvent & {
                placePrediction?: google.maps.places.PlacePrediction | null;
            };

            // Support both API payload shapes:
            // - gmp-placeselect: event.place
            // - gmp-select: event.placePrediction.toPlace()
            const place = selectEvent.place ?? selectEvent.placePrediction?.toPlace();
            if (!place) return;

            await place.fetchFields({ fields: ["location"] });
            const loc = place.location;
            if (!loc) return;

            onSelected({ lat: loc.lat(), lng: loc.lng() });
        };

        // Attach both to be resilient across Places widget versions/channels.
        placeAutocomplete.addEventListener("gmp-select", onPlaceSelect);
        placeAutocomplete.addEventListener("gmp-placeselect", onPlaceSelect);
    }

    async bindAddressAutocompleteAsync(
        input: HTMLInputElement,
        options: { at?: LatLng; radiusMeters?: number },
        onSelected: (selected: GeoAddress) => void
    ): Promise<void> {
        // Places lib needed for Autocomplete widget
        await this._loader.importLibrary("places");

        // cleanup prior Autocomplete binding only (do NOT affect SearchBox)
        this._unbindAutocomplete();

        const ac = new google.maps.places.Autocomplete(input, {
            types: ["address"],
            fields: ["geometry", "address_components", "formatted_address"],
            strictBounds: false
        });

        // Bias (meters, more correct than degrees)
        if (options.at) {
            const circle = new google.maps.Circle({
                center: options.at,
                radius: options.radiusMeters ?? 20000
            });

            const b = circle.getBounds();
            if (b) ac.setBounds(b);
        }

        this._autocompleteListener = ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            const loc = place.geometry?.location;
            if (!loc) return;

            const coords: LatLng = { lat: loc.lat(), lng: loc.lng() };

            onSelected(
                this._mapGoogleComponentsToGeoAddress(
                    coords,
                    place.formatted_address ?? null,
                    place.address_components ?? null
                )
            );
        });
    }

    async geocodeAsync(query: string): Promise<LatLng | null> {
        // Ensures Maps JS is loaded (Geocoder lives on google.maps)
        await this._loader.importLibrary("maps");
        this._geocoder ??= new google.maps.Geocoder();

        return await new Promise<LatLng | null>((resolve) => {
            this._geocoder!.geocode({ address: query }, (results, status) => {
                if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                    resolve(null);
                    return;
                }

                const loc = results[0].geometry.location;
                resolve({ lat: loc.lat(), lng: loc.lng() });
            });
        });
    }

    async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        await this._loader.importLibrary("maps");
        this._geocoder ??= new google.maps.Geocoder();

        return await new Promise<GeoAddress | null>((resolve) => {
            this._geocoder!.geocode({ location: coords }, (results, status) => {
                if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                    resolve(null);
                    return;
                }

                const best = results[0];
                resolve(
                    this._mapGoogleComponentsToGeoAddress(
                        coords,
                        best.formatted_address ?? null,
                        best.address_components ?? null
                    )
                );
            });
        });
    }

    // Optional if you want cleanup when leaving the page
    dispose(): void {
        // Remove both bindings
        this._unbindSearchBox();
        this._unbindAutocomplete();
    }

    private _unbindSearchBox(): void {
        if (this._searchBoxListener) {
            this._searchBoxListener.remove();
            this._searchBoxListener = null;
        }

        // only possible if you change interface to return unsubscribe
        if (this._boundsUnsub) {
            this._boundsUnsub();
            this._boundsUnsub = null;
        }
    }

    private _unbindAutocomplete(): void {
        if (this._autocompleteListener) {
            this._autocompleteListener.remove();
            this._autocompleteListener = null;
        }
    }

    private _mapGoogleComponentsToGeoAddress(
        coords: LatLng,
        formatted: string | null | undefined,
        components: google.maps.GeocoderAddressComponent[] | null | undefined
    ): GeoAddress {
        const comps = components ?? [];

        const getLong = (type: string): string | undefined =>
            comps.find(c => c.types.includes(type))?.long_name ?? undefined;

        const getShort = (type: string): string | undefined =>
            comps.find(c => c.types.includes(type))?.short_name ?? undefined;

        // City fallbacks across locales
        const city =
            getLong("locality") ??
            getLong("postal_town") ??
            getLong("administrative_area_level_3") ??
            getLong("administrative_area_level_2");

        // Neighborhood-ish fields
        const district =
            getLong("neighborhood") ??
            getLong("sublocality_level_1") ??
            getLong("sublocality");

        const sublocality =
            getLong("sublocality_level_1") ??
            getLong("sublocality");

        // Street naming (route has both long & short)
        const street = getLong("route");
        const streetShortName = getShort("route") ?? street;

        return {
            coords,
            label: formatted ?? undefined,

            countryCode: getShort("country"),
            countryName: getLong("country"),

            stateCode: getShort("administrative_area_level_1"),
            stateName: getLong("administrative_area_level_1"),

            city,
            postalCode: getLong("postal_code"),

            street,
            streetShortName,

            houseNumber: getLong("street_number"),

            district,
            sublocality
        };
    }
}